import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';

interface Props {
  element: DocumentElementType;
}

/** Parse the pipe-delimited content into a grid of trimmed cells. */
const parseRows = (content: string): string[][] =>
  content
    .split('\n')
    .map((line) => line.split('|').map((c) => c.trim()))
    .filter((row) => row.some((c) => c !== ''));

/**
 * Table (Elements Library): a static, read-only info table. Content is
 * authored as one row per line with columns separated by "|"; the first
 * row is the header. No value; display-only.
 */
const InfoTable: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    content?: string;
    hidden?: boolean;
  };

  const label = data?.label;
  const rows = parseRows(data?.content || '');
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const header = rows[0] ?? [];
  const body = rows.slice(1);
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
  } as React.CSSProperties;

  return (
    <div className="space-y-2">
      {label && (
        <div className="text-[14px] font-bold text-[var(--forms-ink)]">
          {label}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded-[11px] border border-dashed border-[var(--forms-border)] px-[13px] py-[10px] text-[12.5px] italic text-[var(--forms-muted-soft)]">
          No table content yet
        </div>
      ) : (
        <div className="overflow-hidden rounded-[11px] border border-[#e6e9ee] text-[13px]">
          <div
            style={gridStyle}
            className="border-b border-[#eef1f4] bg-[#f7f9fb]"
          >
            {Array.from({ length: cols }, (_, i) => (
              <span
                key={i}
                className="px-[13px] py-[9px] font-bold text-[#5a6472]"
              >
                {header[i] ?? ''}
              </span>
            ))}
          </div>
          {body.map((row, r) => (
            <div
              key={r}
              style={gridStyle}
              className={
                r < body.length - 1 ? 'border-b border-[#f2f4f7]' : undefined
              }
            >
              {Array.from({ length: cols }, (_, i) => (
                <span
                  key={i}
                  className="px-[13px] py-[9px] text-[var(--forms-ink)]"
                >
                  {row[i] ?? ''}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default InfoTable;
